import { type KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CornerDownLeft, Search } from 'lucide-react';
import type { CustomersGateway } from '@forq/customers-data';
import type { MachinesGateway } from '@forq/machines-data';
import type { OrderGateway } from '@forq/orders-data';

export interface CommandGateways {
  readonly customers: CustomersGateway;
  readonly machines: MachinesGateway;
  readonly orders: OrderGateway;
}

interface CommandPaletteProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly tenantId: string;
  readonly permissions: ReadonlySet<string>;
  readonly gateways: CommandGateways;
}

interface CommandResult {
  readonly key: string;
  readonly group: string;
  readonly title: string;
  readonly subtitle: string;
  readonly to: string;
}

const groupOrder = ['Kunden', 'Maschinen', 'Vorgänge'] as const;

/**
 * FORQ Command — the global search region. Ctrl/⌘+K, tenant- and permission-aware search
 * across the core entities; every hit is a deep link into the record's own URL.
 */
export function CommandPalette({ open, onClose, tenantId, permissions, gateways }: CommandPaletteProps) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<readonly CommandResult[]>([]);
  const [active, setActive] = useState(0);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setResults([]);
    setActive(0);
    const timer = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(timer);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const term = query.trim();
    if (!term) {
      setResults([]);
      setBusy(false);
      return undefined;
    }
    setBusy(true);
    let live = true;
    const timer = window.setTimeout(() => {
      const tasks: Promise<CommandResult[]>[] = [];
      if (permissions.has('customers.view')) {
        tasks.push(gateways.customers
          .searchCustomers({ tenantId, query: term, status: 'all', pageSize: 5 })
          .then((page) => page.items.map((customer) => ({
            key: `c-${customer.id}`,
            group: 'Kunden',
            title: customer.displayName,
            subtitle: `${customer.number}${customer.city ? ` · ${customer.city}` : ''}`,
            to: `/kunden/${customer.number}`,
          }))));
      }
      if (permissions.has('machines.view')) {
        tasks.push(gateways.machines
          .searchMachines({ tenantId, query: term, status: 'all', typeId: 'all', pageSize: 5 })
          .then((page) => page.items.map((machine) => ({
            key: `m-${machine.id}`,
            group: 'Maschinen',
            title: `${machine.manufacturer} ${machine.model}`.trim() || machine.number,
            subtitle: `${machine.number}${machine.typeName ? ` · ${machine.typeName}` : ''}`,
            to: `/maschinen/${machine.number}`,
          }))));
      }
      if (permissions.has('orders.view')) {
        tasks.push(gateways.orders
          .searchOrders({ tenantId, query: term, bucket: 'all', pageSize: 5 })
          .then((page) => page.items.map((order) => ({
            key: `o-${order.id}`,
            group: 'Vorgänge',
            title: order.title || order.number,
            subtitle: `${order.number}${order.customerName ? ` · ${order.customerName}` : ''}`,
            to: `/vorgaenge/${order.number}`,
          }))));
      }
      Promise.all(tasks)
        .then((groups) => {
          if (!live) return;
          setResults(groups.flat());
          setActive(0);
          setBusy(false);
        })
        .catch(() => {
          if (!live) return;
          setResults([]);
          setBusy(false);
        });
    }, 200);
    return () => {
      live = false;
      window.clearTimeout(timer);
    };
  }, [open, query, tenantId, permissions, gateways]);

  const grouped = useMemo(
    () => groupOrder
      .map((label) => ({ label, items: results.filter((result) => result.group === label) }))
      .filter((group) => group.items.length > 0),
    [results],
  );

  const go = useCallback((to: string) => {
    onClose();
    navigate(to);
  }, [navigate, onClose]);

  const handleKeyDown = useCallback((event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      onClose();
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive((current) => Math.min(current + 1, Math.max(results.length - 1, 0)));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((current) => Math.max(current - 1, 0));
    } else if (event.key === 'Enter') {
      const result = results[active];
      if (result) go(result.to);
    }
  }, [results, active, go, onClose]);

  if (!open) return null;

  return (
    <div
      className="forq-command"
      role="dialog"
      aria-modal="true"
      aria-label="FORQ Command"
      onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}
    >
      <div className="forq-command__panel" onKeyDown={handleKeyDown}>
        <div className="forq-command__search">
          <Search size={17} />
          <input
            ref={inputRef}
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Kunden, Maschinen oder Vorgänge suchen …"
            aria-label="Global suchen"
          />
          <kbd>Esc</kbd>
        </div>
        <div className="forq-command__results">
          {!query.trim() ? (
            <div className="forq-command__hint">Über alle berechtigten Bereiche suchen. <kbd>↑</kbd><kbd>↓</kbd> wählen, <kbd>⏎</kbd> öffnen.</div>
          ) : busy && results.length === 0 ? (
            <div className="forq-command__hint">Sucht …</div>
          ) : results.length === 0 ? (
            <div className="forq-command__hint">Keine Treffer für „{query.trim()}".</div>
          ) : (
            grouped.map((group) => (
              <div className="forq-command__group" key={group.label}>
                <div className="forq-command__group-label">{group.label}</div>
                {group.items.map((result) => {
                  const index = results.indexOf(result);
                  return (
                    <button
                      type="button"
                      key={result.key}
                      className={`forq-command__item${index === active ? ' is-active' : ''}`}
                      onMouseMove={() => setActive(index)}
                      onClick={() => go(result.to)}
                    >
                      <span className="forq-command__item-title">{result.title}</span>
                      <span className="forq-command__item-sub">{result.subtitle}</span>
                      <CornerDownLeft className="forq-command__item-enter" size={13} />
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
